import { useState, useCallback } from 'react';
import { createApiClient, ApiError as ApiClientError } from '@/lib/adminApiClient';
import type { ApiComponents, ApiPaths } from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

// 排班/预约端点都受 RBAC + 租户隔离保护；typed client 必须通过共享的
// tokenStorage 流向请求附加 `Authorization: Bearer <accessToken>`（Issue #226）。
const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

// Type aliases for OpenAPI DTOs
type AppointmentDto = ApiComponents['schemas']['AppointmentDto'];
type CreateAppointmentDto = ApiComponents['schemas']['CreateAppointmentDto'];
type UpdateAppointmentDto = ApiComponents['schemas']['UpdateAppointmentDto'];
type TransitionAppointmentStatusDto = ApiComponents['schemas']['TransitionAppointmentStatusDto'];
type StaffScheduleDto = ApiComponents['schemas']['StaffScheduleDto'];
type UpsertStaffScheduleDto = ApiComponents['schemas']['UpsertStaffScheduleDto'];

// Derive the status type from the OpenAPI schema
type AppointmentStatus = ApiComponents['schemas']['AppointmentDto']['status'];

// Derive list query params type from OpenAPI paths
type ListAppointmentsQuery = NonNullable<
  ApiPaths['/api/v1/scheduling/appointments']['get']['parameters']['query']
>;

export interface ApiError {
  message: string;
  code?: string;
}

/** Translate a raw API error into a user-friendly ApiError.
 *  HTTP 409 gets a specific conflict message per ISSUE-4. */
function toApiError(err: unknown, httpStatus?: number): ApiError {
  // openapi-fetch middleware throws ApiClientError on non-2xx responses, so
  // the catch path supplies the status via the thrown error instead of via
  // the explicit `httpStatus` argument. Fall back to it when the caller did
  // not pass an explicit status (Issue #226).
  const status = httpStatus ?? (err instanceof ApiClientError ? err.status : undefined);
  if (status === 401) {
    return { message: '会话已失效，请重新登录后再试', code: 'UNAUTHENTICATED' };
  }
  if (status === 403) {
    return { message: '当前角色没有排班/预约模块访问权限', code: 'FORBIDDEN' };
  }
  if (status === 409) {
    return {
      message: '数据已被其他用户修改，请刷新页面后重试',
      code: 'CONFLICT',
    };
  }
  if (err instanceof Error) {
    return { message: err.message };
  }
  return { message: String(err) };
}

// ============================================================================
// Appointment hooks
// ============================================================================

export function useAppointments() {
  const [appointments, setAppointments] = useState<AppointmentDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchAppointments = useCallback(
    async (params?: Partial<ListAppointmentsQuery>) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.GET('/api/v1/scheduling/appointments', {
          params: { query: params as ListAppointmentsQuery },
        });
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return;
        }
        setAppointments([...(data ?? [])]);
      } catch (e) {
        setError(toApiError(e));
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { appointments, loading, error, fetchAppointments };
}

export function useAppointmentDetail() {
  const [appointment, setAppointment] = useState<AppointmentDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchAppointment = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.GET('/api/v1/scheduling/appointments/{id}', {
        params: { path: { id } },
      });
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      setAppointment(data ?? null);
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  return { appointment, loading, error, fetchAppointment };
}

export function useCreateAppointment() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const createAppointment = useCallback(async (dto: CreateAppointmentDto): Promise<AppointmentDto | null> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST('/api/v1/scheduling/appointments', {
        body: dto,
      });
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { createAppointment, loading, error };
}

export function useUpdateAppointment() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const updateAppointment = useCallback(async (id: string, dto: UpdateAppointmentDto): Promise<AppointmentDto | null> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.PATCH('/api/v1/scheduling/appointments/{id}', {
        params: { path: { id } },
        body: dto,
      });
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { updateAppointment, loading, error };
}

export function useTransitionAppointment() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const confirmAppointment = useCallback(async (id: string, dto: TransitionAppointmentStatusDto): Promise<AppointmentDto | null> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST('/api/v1/scheduling/appointments/{id}/confirm', {
        params: { path: { id } },
        body: dto,
      });
      if (apiErr) { setError(toApiError(apiErr, (response as Response)?.status)); return null; }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally { setLoading(false); }
  }, []);

  const cancelAppointment = useCallback(async (id: string, dto: TransitionAppointmentStatusDto): Promise<AppointmentDto | null> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST('/api/v1/scheduling/appointments/{id}/cancel', {
        params: { path: { id } },
        body: dto,
      });
      if (apiErr) { setError(toApiError(apiErr, (response as Response)?.status)); return null; }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally { setLoading(false); }
  }, []);

  const checkInAppointment = useCallback(async (id: string, dto: TransitionAppointmentStatusDto): Promise<AppointmentDto | null> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST('/api/v1/scheduling/appointments/{id}/check-in', {
        params: { path: { id } },
        body: dto,
      });
      if (apiErr) { setError(toApiError(apiErr, (response as Response)?.status)); return null; }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally { setLoading(false); }
  }, []);

  const completeAppointment = useCallback(async (id: string, dto: TransitionAppointmentStatusDto): Promise<AppointmentDto | null> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST('/api/v1/scheduling/appointments/{id}/complete', {
        params: { path: { id } },
        body: dto,
      });
      if (apiErr) { setError(toApiError(apiErr, (response as Response)?.status)); return null; }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally { setLoading(false); }
  }, []);

  const noShowAppointment = useCallback(async (id: string, dto: TransitionAppointmentStatusDto): Promise<AppointmentDto | null> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST('/api/v1/scheduling/appointments/{id}/no-show', {
        params: { path: { id } },
        body: dto,
      });
      if (apiErr) { setError(toApiError(apiErr, (response as Response)?.status)); return null; }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally { setLoading(false); }
  }, []);

  return {
    confirmAppointment,
    cancelAppointment,
    checkInAppointment,
    completeAppointment,
    noShowAppointment,
    loading,
    error,
  };
}

// ============================================================================
// Staff Schedule hooks
// ============================================================================

export function useStaffSchedules() {
  const [schedules, setSchedules] = useState<StaffScheduleDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchSchedulesByDate = useCallback(async (date: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.GET('/api/v1/scheduling/staff-schedules', {
        params: { query: { date } },
      });
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      setSchedules([...(data ?? [])]);
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  return { schedules, loading, error, fetchSchedulesByDate };
}

export function useUpsertStaffSchedule() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const upsertSchedule = useCallback(async (staffId: string, date: string, dto: UpsertStaffScheduleDto): Promise<StaffScheduleDto | null> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.PUT('/api/v1/scheduling/staff-schedules/{staffId}/{date}', {
        params: { path: { staffId, date } },
        body: dto,
      });
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { upsertSchedule, loading, error };
}

// Re-export status type for use in pages
export type { AppointmentStatus };
